import { Stack, Text, Tooltip } from '@mantine/core';
import { ReactElement, ReactNode } from 'react';
import { Track, TrackLabel } from '@bariendo/lib/constants';
import { isEmployerSponsoredTrack, isPartnerTrack } from '@bariendo/lib/pac-inquiry-list';
import { TrackPill } from '@/pages/PatientTimelinePage/PatientTimelineHeader/TrackPill';
import { shortEmployerName } from '../services/employerName';

/** The prototype's exception amber. */
export const QUEUE_WARN_COLOR = '#8A5A12';

/** Its counterpart, for Won's scheduled procedure. */
export const QUEUE_OK_COLOR = 'var(--ev-color-green-dark, #243721)';

/** A done checklist item's tick (the prototype's `--state-ok`). */
export const QUEUE_DONE_TICK_COLOR = '#1F8F4D';

/** An empty value's dash, or "No chart note": lighter than a qualifier. */
export const QUEUE_EMPTY_COLOR = 'rgba(0, 0, 0, 0.35)';

const SECONDARY_COLOR = 'rgba(0, 0, 0, 0.55)';

const LINE_STYLE = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } as const;

// Line 1 sits in a 24px band, the height of an avatar or note, so every cell's text lines up.
const PRIMARY_STYLE = { ...LINE_STYLE, padding: '3px 0' } as const;
const SECONDARY_STYLE = { ...LINE_STYLE, marginTop: 3 } as const;

export interface TwoLineCellProps {
  /** What the cell is: 13px black. */
  primary: ReactNode;
  /** What qualifies it: 12px grey. Omitted, the cell is one line. */
  secondary?: ReactNode;
  primaryEmphasis?: 'warning';
  secondaryEmphasis?: 'warning';
}

/** The Intake hub's row grammar: identity over qualifier, no field labels. */
export function TwoLineCell({
  primary,
  secondary,
  primaryEmphasis,
  secondaryEmphasis,
}: TwoLineCellProps): ReactElement {
  return (
    <Stack gap={0} style={{ minWidth: 0 }}>
      <Text
        component="div"
        fz={13}
        lh="18px"
        c={primaryEmphasis === 'warning' ? QUEUE_WARN_COLOR : 'black'}
        style={PRIMARY_STYLE}
      >
        {primary}
      </Text>
      {secondary !== undefined && (
        <Text
          component="div"
          fz={12}
          lh="16px"
          c={secondaryEmphasis === 'warning' ? QUEUE_WARN_COLOR : SECONDARY_COLOR}
          style={SECONDARY_STYLE}
        >
          {secondary}
        </Text>
      )}
    </Stack>
  );
}

export interface PaymentTrackCellProps {
  track: Track | undefined;
  /** The employer the lead named, when there is one. */
  employer?: string;
  /** The insurer, under an insurance pill, where the tab carries one (To schedule, BAR-3096). */
  insurer?: string;
}

/**
 * The pill, and for an employer row the administrator then the employer:
 * `Carrum · Delta Air…`. Self-pay has nothing to qualify and stays a bare pill.
 */
export function PaymentTrackCell({ track, employer, insurer }: PaymentTrackCellProps): ReactElement {
  if (!track) {
    return <TwoLineCell primary={<span style={{ color: QUEUE_EMPTY_COLOR }}>—</span>} />;
  }

  const employerSponsored = isEmployerSponsoredTrack(track);
  const administrator = isPartnerTrack(track) ? TrackLabel[track] : undefined;
  const shortEmployer = employer ? shortEmployerName(employer) : undefined;
  const qualifier = employerSponsored
    ? [administrator, shortEmployer].filter(Boolean).join(' · ')
    : track === Track.INSURANCE
      ? (insurer ?? '')
      : '';

  return (
    <TwoLineCell
      primary={<TrackPill track={employerSponsored ? Track.EMPLOYER : track} variant="queue" />}
      secondary={
        qualifier ? (
          <Tooltip
            label={employer}
            disabled={!employerSponsored || !employer || shortEmployer === employer}
            withArrow
            position="top-start"
          >
            <span>{qualifier}</span>
          </Tooltip>
        ) : undefined
      }
    />
  );
}
